import { useMemo, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  Users,
  BarChart3,
  House,
  Search,
  Bell,
  Shield,
  Plus,
  FolderPlus,
  Activity,
  ShieldCheck,
  LogOut,
} from 'lucide-react';

import { signOut } from '@/features/auth/api';
import { useSearchPalette } from '@/features/search/SearchPaletteProvider';
import { useMyProfile } from '@/features/profile/hooks/useMyProfile';
import { useUnreadCount } from '@/features/notifications/hooks/useNotifications';
import { useHomeSummary } from '@/features/home/hooks/useHome';
import { usePendingModeration } from '@/features/moderation/hooks/useModeration';
import { useNuevoGasto } from '@/shared/hooks/useNuevoGasto';
import { ATAJO_DE_BUSCAR } from '@/shared/hooks/useGlobalShortcuts';
import { useEsEscritorio } from '@/shared/layout/breakpoints';
import { GroupPickerSheet } from '@/shared/components/GroupPickerSheet';
import { AndroidAppLink } from '@/features/install/AndroidAppLink';
import { detectPlatform } from '@/features/install/useInstallPWA';
import { InstallPrompt } from '@/features/install/InstallPrompt';
import { UpdateRow } from '@/shared/components/UpdatePrompt';

/** Cuántos grupos recientes caben bajo «Grupos» sin que el menú deje de ser un
 *  menú. Con más, la lista se convierte en la página de grupos, que ya existe
 *  un clic más allá. */
const GRUPOS_RECIENTES = 3;

/**
 * La navegación de escritorio, a partir de `lg` (1024 px).
 *
 * Por debajo de ese ancho no se monta y manda la barra inferior de siempre: en
 * un teléfono la navegación va al alcance del pulgar, y una lateral fija se
 * comería un tercio de la pantalla. El corte en `lg` y no en `md` es a
 * propósito — una tablet en vertical son 768 px y ahí la barra inferior sigue
 * siendo mejor.
 *
 * Lo que la lateral gana respecto a la inferior no es sitio, es **contexto
 * permanente**: se ve dónde estás sin tener que mirar el título de la página, y
 * caben destinos que en la inferior no cabían —notificaciones y administración
 * viven hoy escondidos en el TopBar y en el perfil.
 */
export function Sidebar() {
  const { t } = useTranslation();
  const { data: profile } = useMyProfile();
  const { data: unread = 0 } = useUnreadCount();
  const searchPalette = useSearchPalette();
  const gasto = useNuevoGasto();
  const esEscritorio = useEsEscritorio();
  const esSuperAdmin = profile?.is_super_admin === true;
  // La lateral empieza en 1024px y un iPad apaisado son 1180: sin esta guarda,
  // a un iPad se le ofrecía la app de Android. En móvil ya estaba mirado
  // (`App.tsx`, `isAndroid`); aquí se montaba sin condición porque «escritorio»
  // se dio por sentado que era un ordenador. Se pregunta por iOS y no por
  // Android porque en un ordenador el enlace **sí** tiene sentido: es el
  // momento más natural para llevarse la app al teléfono.
  const esIOS = useMemo(() => detectPlatform() === 'ios', []);

  // `my_home_summary` ya ordena los grupos por última actividad y es la única
  // consulta que sabe cuándo se movió cada uno —`my_groups()` sólo tiene
  // `created_at`, que es otra cosa: el grupo del viaje del año pasado no deja
  // de ser el más nuevo por llevar meses parado—.
  //
  // Sin argumento a propósito: con el límite por defecto la clave es la misma
  // que usan Inicio y Grupos, así que las tres comparten **una** petición en vez
  // de pedir lo mismo tres veces con distinto tamaño de cola de actividad.
  const { data: resumen } = useHomeSummary(undefined, esEscritorio);
  const recientes = (resumen?.groups ?? [])
    .filter((g) => !g.is_archived)
    .slice(0, GRUPOS_RECIENTES);

  const { data: moderacion } = usePendingModeration(undefined, esSuperAdmin);
  const pendientesModeracion = moderacion?.total ?? 0;

  // Con un grupo abierto siempre se puede: el gasto va a ése. Sin grupo abierto
  // hace falta tener alguno, porque un gasto sin grupo no existe. Mientras la
  // consulta está en vuelo se deja **habilitado**: apagarlo medio segundo y
  // encenderlo después es peor que dejarlo, porque para entonces quien miraba ya
  // ha decidido que no se puede pulsar.
  const puedeCrearGasto = gasto.dentroDeGrupo || gasto.hayGrupos || gasto.cargando;

  return (
    <aside
      className="fixed bottom-0 left-0 z-40 hidden w-60 flex-col border-r border-neutral-200 bg-white lg:flex"
      // Arranca justo debajo del `TopBar`, que es fijo, mide 2 rem y va con
      // `z-[9999]`: con `inset-y-0` la cabecera de la lateral quedaba tapada
      // por él. Misma cuenta que el `paddingTop` del contenedor de `App`.
      style={{ top: 'calc(env(safe-area-inset-top) + 2rem)' }}
      aria-label={t('nav.primary', { defaultValue: 'Navegación principal' })}
    >
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-neutral-200 px-4">
        {/* 20px y seminegrita, igual que `.sidebar-brand-name` de ManuJob. La
            familia ya coincidía sin saberlo: `font-serif` está redefinida a IBM
            Plex Mono en `tailwind.config`, que es la misma de allí — sólo
            faltaban el tamaño y el grosor.

            Y con esto los destinos **parecen** más pequeños sin serlo: eran
            14px antes y lo siguen siendo, los mismos que `.sidebar-item` de
            ManuJob. Lo que se veía distinto era el contraste con un titular de
            16px donde allí hay uno de 20. */}
        <span className="font-serif text-xl font-semibold text-brand">ManuSplit</span>
      </div>

      {/* Las acciones, arriba del todo y separadas de los destinos.
          En móvil «Nuevo gasto» es un botón flotante sobre el contenido, que es
          donde llega el pulgar; aquí eso no aplica —el ratón está donde se le
          deje— y un círculo en la esquina inferior derecha queda lejos de todo y
          tapando la lista. Con un menú permanente delante, lo que se hace a
          menudo se busca arriba. */}
      {!gasto.oculto && (
        <div className="shrink-0 border-b border-neutral-200 p-3">
          <button
            type="button"
            data-atajo="nuevo-gasto"
            onClick={gasto.ir}
            disabled={!puedeCrearGasto}
            title={puedeCrearGasto ? undefined : t('expenses.no_groups_yet')}
            className="bg-brand flex w-full items-center justify-center gap-2 rounded px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus size={16} />
            {t('expenses.new_title')}
          </button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto p-2.5">
        <ul className="space-y-0.5">
          <SidebarLink to="/" icon={<House size={18} />} label={t('nav.home')} end />
          {/* Los grupos recientes cuelgan **dentro** del `<li>` de Grupos y no
              detrás: una lista anidada que es hermana de su encabezado se lee
              como otra lista cualquiera. */}
          <SidebarLink to="/groups" icon={<Users size={18} />} label={t('nav.groups')}>
            <ul className="ml-[1.55rem] space-y-0.5 border-l border-neutral-200 pl-2">
              {recientes.map((g) => (
                <li key={g.group_id}>
                  <NavLink
                    to={`/groups/${g.group_id}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded px-2 py-1 text-xs transition-colors ${
                        isActive
                          ? 'text-brand font-medium'
                          : 'text-muted hover:bg-background hover:text-ink'
                      }`
                    }
                  >
                    <span aria-hidden="true">{g.group_emoji ?? '📊'}</span>
                    <span className="truncate">{g.group_name}</span>
                  </NavLink>
                </li>
              ))}
              {/* Al final de los grupos y no arriba con «Nuevo gasto»: crear un
                  grupo se hace una vez cada muchos meses, y estaba ocupando el
                  sitio de lo que se hace a diario. Aquí además aparece justo
                  donde se acaba de mirar que el grupo que se busca no está —y
                  cuando no hay ninguno, es lo único que hay—. */}
              <li>
                <NavLink
                  to="/groups/new"
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded px-2 py-1 text-xs transition-colors ${
                      isActive
                        ? 'text-brand font-medium'
                        : 'text-muted hover:bg-background hover:text-ink'
                    }`
                  }
                >
                  <FolderPlus size={13} />
                  <span className="truncate">{t('groups.create')}</span>
                </NavLink>
              </li>
            </ul>
          </SidebarLink>
          {/* Faltaba, y tiene página propia desde hace tiempo: en la barra
              inferior no cabía y aquí sobra sitio. Es justo lo que gana una
              lateral respecto a una de cuatro huecos. */}
          <SidebarLink to="/activity" icon={<Activity size={18} />} label={t('nav.activity')} />
          <SidebarLink to="/stats" icon={<BarChart3 size={18} />} label={t('nav.stats')} />
          <SidebarLink
            to="/notifications"
            icon={<Bell size={18} />}
            label={t('nav.notifications', { defaultValue: 'Avisos' })}
            badge={unread}
          />
          {/* Perfil y administración iban bajo un encabezado «Tú». Se quitó:
              con seis destinos —ocho para un superadministrador—, un rótulo que
              aparta el último del resto no agrupa nada, porque son todos sitios
              a los que se va. Y costaba una fila de alto más el `mt-5` de aire.
              La lista es corta; el rótulo sobraba. */}
          <SidebarLink to="/profile" icon={<User size={18} />} label={t('nav.profile')} />
          {/* Importar ya no está aquí: se hace una vez, al llegar desde
              Splitwise, y ocupaba un hueco permanente para eso. Sigue donde
              estaba, dentro de Perfil. */}
          {esSuperAdmin && (
            <>
              <SidebarLink
                to="/admin"
                icon={<Shield size={18} />}
                label={t('nav.admin', { defaultValue: 'Administración' })}
                end
              />
              {/* Con el contador: la cola de moderación es trabajo que llega
                  solo, sin que nadie avise, y un enlace sin número obliga a
                  entrar a comprobar si hay algo. */}
              <SidebarLink
                to="/admin/moderation"
                icon={<ShieldCheck size={18} />}
                label={t('moderation.title')}
                badge={pendientesModeracion}
              />
            </>
          )}
        </ul>
      </nav>

      {gasto.selectorAbierto && <GroupPickerSheet onClose={gasto.cerrarSelector} />}

      {/* El enlace a la app, en el pie y no entre los destinos: tampoco es un
          sitio al que se va. Se cierra y no vuelve; el detalle, en el
          componente. */}
      {/* Instalar y llevarse la app, juntos y en el pie. El globo flotante de
          instalar es `lg:hidden` desde que existe esta fila: teniendo un sitio
          permanente donde ponerlo, flotar por encima del contenido era ruido —
          el mismo criterio por el que `NewExpenseFAB` desaparece aquí. */}
      {/* Encima de instalar y de buscar, que es lo más cerca del final que se
          puede estar sin quedar debajo de todo: una versión nueva caduca —el
          código que se está ejecutando ya no es el que hay— y las otras filas
          no. Se pinta sola cuando toca y no ocupa nada el resto del tiempo. */}
      <UpdateRow />

      <div className="shrink-0 border-t border-neutral-100 px-2.5 pt-2">
        <InstallPrompt variant="row" />
        {!esIOS && <AndroidAppLink />}
      </div>

      {/* Abajo del todo y no entre los destinos: buscar y salir no son sitios a
          los que se va, son acciones. Mezclarlas con los enlaces haría que el
          indicador de «dónde estoy» no tuviera nada que señalar cuando se
          pulsan.

          Las dos comparten bloque en vez de tener uno cada una: son lo mismo
          —acciones— y un tercer `border-t` en el pie convertía el borde en
          decoración en vez de en separación. */}
      <div className="shrink-0 border-t border-neutral-100 p-2.5">
        <button
          type="button"
          onClick={searchPalette.open}
          className="text-muted hover:bg-neutral-100 hover:text-brand flex w-full items-center gap-2 rounded-md px-3 py-2 text-[13px] transition-colors"
        >
          <Search size={18} />
          <span className="flex-1 text-left">{t('nav.search', { defaultValue: 'Buscar' })}</span>
          <kbd className="text-muted rounded border border-neutral-200 px-1.5 py-0.5 font-mono text-[10px]">
            {ATAJO_DE_BUSCAR}
          </kbd>
        </button>

        {/* Cerrar sesión sólo estaba al final de Perfil, y eso vale en un móvil
            —que es de una persona— pero no en un ordenador prestado o de la
            oficina, donde salir es lo que se busca al terminar y hacen falta
            dos clics para encontrarlo.

            No pide confirmación: el daño de salir sin querer es volver a
            entrar, y un diálogo delante de una salida es exactamente lo que
            sobra cuando alguien tiene prisa por dejar libre un PC ajeno.

            Llamada suelta y no `useMutation` como en Perfil: allí la mutación
            existe para apagar un botón grande mientras va, y aquí no hay nada
            que enseñar —`signOut` se traga su propio error y la sesión se cierra
            en local igual—. Envolver la lateral en un `QueryClientProvider`
            para no usar nada de él era pagar por un estado que nadie mira. */}
        <button
          type="button"
          onClick={() => void signOut()}
          className="text-muted hover:bg-neutral-100 hover:text-brand flex w-full items-center gap-2 rounded-md px-3 py-2 text-[13px] transition-colors"
        >
          <LogOut size={18} />
          <span className="flex-1 text-left">{t('auth.sign_out')}</span>
        </button>
      </div>
    </aside>
  );
}

function SidebarLink({
  to,
  icon,
  label,
  end,
  badge,
  children,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  end?: boolean;
  badge?: number;
  /** Sublista, dentro del mismo `<li>` que su enlace. */
  children?: ReactNode;
}) {
  return (
    <li>
      <NavLink
        to={to}
        end={end ?? false}
        className={({ isActive }) =>
          `flex items-center gap-2 rounded-md px-3 py-[9px] text-sm transition-colors ${
            isActive
              ? 'bg-brand font-medium text-white hover:bg-brand-600'
              : 'text-ink hover:bg-neutral-100'
          }`
        }
      >
        {({ isActive }) => (
          <>
            {icon}
            <span className="flex-1 truncate">{label}</span>
            {badge != null && badge > 0 && (
              /* `.sidebar-badge` de ManuJob. **Lo que cambia sobre el destino
                 activo es el fondo, no la letra**: `#8B2C2C` se mantiene y el
                 `#FBEDED` pasa a blanco, porque un rosa pálido sobre el azul de
                 marca no se distingue. Es literalmente lo que hace
                 `.sidebar-item-active .sidebar-badge` allí.
                 `children` de `NavLink` pasa a función porque es la única forma
                 de saber aquí dentro si el destino está activo — el `className`
                 ya lo recibía, pero el color de la insignia no vive ahí. */
              <span
                className={`min-w-5 shrink-0 rounded-[10px] px-1.5 py-px text-center text-[11px] font-semibold text-[#8B2C2C] ${
                  isActive ? 'bg-white/90' : 'bg-[#FBEDED]'
                }`}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </>
        )}
      </NavLink>
      {children}
    </li>
  );
}
